import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Spinner } from '../design-system';
import {
  isValidCode,
  listVerifiedFactors,
  removeFactor,
  startEnrollment,
  verifyCode,
  type Enrollment,
  type VerifiedFactor,
} from '../../lib/mfa';
import { notifyTwoFaChanged } from '../../lib/twoFa';

/**
 * Two-factor authentication for the signed-in person: turn it on with an
 * authenticator app (QR code, then a 6-digit code to prove it works), or turn it
 * off. Used in Settings and on the "set it up now" screen.
 */
export function TwoFactorSettings({ onChanged, compact = false }: { onChanged?: () => void; compact?: boolean }) {
  const [factors, setFactors] = useState<VerifiedFactor[] | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setFactors(await listVerifiedFactors());
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't load your two-factor settings.");
      setFactors([]);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function begin() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      setEnrollment(await startEnrollment());
      setCode('');
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't start the setup.");
    }
    setBusy(false);
  }

  async function confirm() {
    if (!enrollment) return;
    setBusy(true);
    setError(null);
    try {
      await verifyCode(enrollment.factorId, code);
      setEnrollment(null);
      setDone('Two-factor authentication is on.');
      await load();
      notifyTwoFaChanged();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That code did not work.');
    }
    setBusy(false);
  }

  async function turnOff(factorId: string) {
    setBusy(true);
    setError(null);
    try {
      await removeFactor(factorId);
      setConfirmOff(null);
      setDone('Two-factor authentication is off.');
      await load();
      notifyTwoFaChanged();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't turn it off.");
    }
    setBusy(false);
  }

  if (factors === null) return <div className="flex justify-center py-4"><Spinner /></div>;

  const on = factors.length > 0;
  return (
    <div className="flex flex-col gap-3">
      {!compact && (
        <p className="text-sm text-app-muted">
          A second step at sign-in: besides your password, you enter a 6-digit code from an authenticator app on your phone
          (Google Authenticator, 1Password, Authy…). It keeps your account safe even if your password leaks.
        </p>
      )}

      {done && <p role="status" className="text-sm text-emerald-500">{done}</p>}

      {!enrollment && (
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${on ? 'bg-emerald-500/15 text-emerald-500' : 'bg-app-border text-app-muted'}`}>
            {on ? 'On' : 'Off'}
          </span>
          {!on && <Button size="sm" onClick={() => void begin()} disabled={busy}>{busy ? <Spinner size="sm" className="text-ink" /> : 'Turn on'}</Button>}
        </div>
      )}

      {enrollment && (
        <div className="flex flex-col gap-3 rounded-xl border border-app-border bg-app-bg p-4">
          <ol className="text-sm text-app-fg list-decimal pl-5 flex flex-col gap-1">
            <li>Open your authenticator app and add an account by scanning this code.</li>
            <li>Type the 6-digit code the app shows.</li>
          </ol>
          <img src={enrollment.qrCode} alt="QR code to add Sekretly to your authenticator app" className="w-44 h-44 bg-white rounded-lg p-2 self-start" />
          <p className="text-xs text-app-muted break-all">
            Can't scan? Enter this key in the app: <span className="font-mono text-app-fg select-all">{enrollment.secret}</span>
          </p>
          <Input
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            maxLength={7}
            onChange={(e) => setCode(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void confirm()} disabled={busy || !isValidCode(code)}>
              {busy ? <Spinner size="sm" className="text-ink" /> : 'Confirm and turn on'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEnrollment(null); setError(null); }} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {on && !enrollment && (
        <ul className="flex flex-col gap-2">
          {factors.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-app-bg px-3 py-2">
              <span className="text-sm text-app-fg">Authenticator app <span className="text-xs text-app-muted">· added {new Date(f.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span></span>
              {confirmOff === f.id ? (
                <span className="flex items-center gap-2">
                  <span className="text-xs text-app-muted">Turn off 2FA?</span>
                  <Button size="sm" variant="secondary" onClick={() => void turnOff(f.id)} disabled={busy}>Yes, turn off</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmOff(null)} disabled={busy}>Keep it</Button>
                </span>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setConfirmOff(f.id)} disabled={busy}>Turn off</Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {on && !enrollment && (
        <div>
          <Button size="sm" variant="ghost" onClick={() => void begin()} disabled={busy}>Add another device</Button>
        </div>
      )}

      {error && <p role="alert" className="text-sm text-crimson">{error}</p>}
    </div>
  );
}
